import React, { useRef } from "react";
import { MobileTabBar } from "./mobile/MobileShell";
import { usePhoneTheme } from "../useIsPhone";

// Desktop sub tab strip, in the coach Team page's TeamSubTabs look: 44px
// high, Archivo 15 bold labels flush left, the orange underline running
// 28px past the label, muted until active. Keyboard as MobileTabBar: Left
// and Right move between tabs (and select them), Home and End jump to the
// ends; only the active tab is in the Tab order (roving tabindex).
//
//   SubTabBar({ tabs: [{ key, label }], value, onChange, ariaLabel, idPrefix, className })
//   SubTabPanel({ idPrefix, tabKey, children, className })
//
// Each tab is role="tab" with id `${idPrefix}-tab-${key}`; the selected one
// also has aria-controls `${idPrefix}-panel-${key}` (only the open panel is
// on the page, so a closed tab never points at an id that is not there, as
// ProfileTabsMobile does with MobileTabBar). SubTabPanel renders that
// role="tabpanel" labelled by its tab (subTabId / subTabPanelId build the
// ids). An unknown `value` leaves the first tab focusable.
//
// Inside the phone theme it renders MobileTabBar (13px labels) with the
// same ids, so one component serves both; a page that already has a phone
// tab strip simply does not render it there.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const subTabId = (idPrefix, key) => `${idPrefix}-tab-${key}`;
export const subTabPanelId = (idPrefix, key) => `${idPrefix}-panel-${key}`;

export const SubTabBar = ({ tabs = [], value, onChange, ariaLabel, idPrefix = "subtabs", className = "" }) => {
  const phone = usePhoneTheme();
  const refs = useRef({});

  if (phone) {
    const mobileTabs = tabs.map((t) => ({
      value: t.key,
      label: t.label,
      id: subTabId(idPrefix, t.key),
      controls: t.key === value ? subTabPanelId(idPrefix, t.key) : undefined,
    }));
    return <MobileTabBar tabs={mobileTabs} value={value} onChange={onChange} ariaLabel={ariaLabel} className={className} />;
  }

  const hasActive = tabs.some((t) => t.key === value);

  const move = (index) => {
    const tab = tabs[(index + tabs.length) % tabs.length];
    if (!tab) return;
    refs.current[tab.key]?.focus();
    if (tab.key !== value) onChange?.(tab.key);
  };

  const onKeyDown = (e, index) => {
    if (e.key === "ArrowRight") move(index + 1);
    else if (e.key === "ArrowLeft") move(index - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(tabs.length - 1);
    else return;
    e.preventDefault();
  };

  return (
    <div role="tablist" aria-label={ariaLabel} className={cx("flex overflow-x-auto border-b border-bm-border", className)}>
      {tabs.map((t, i) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            ref={(el) => {
              refs.current[t.key] = el;
            }}
            id={subTabId(idPrefix, t.key)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={active ? subTabPanelId(idPrefix, t.key) : undefined}
            tabIndex={active || (!hasActive && i === 0) ? 0 : -1}
            onClick={() => onChange?.(t.key)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cx(
              "-mb-px h-11 shrink-0 border-b-2 pr-7 text-left font-archivo text-[15px] font-bold leading-5",
              active ? "border-bm-orange text-bm-orange" : "border-transparent text-bm-muted hover:text-bm-ink",
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
};

export const SubTabPanel = ({ idPrefix = "subtabs", tabKey, children, className = "" }) => (
  <div role="tabpanel" id={subTabPanelId(idPrefix, tabKey)} aria-labelledby={subTabId(idPrefix, tabKey)} tabIndex={0} className={className}>
    {children}
  </div>
);

export default SubTabBar;
